import React, { useMemo, useState } from "react";
import { X, Minus, Plus, Flame } from "lucide-react";
import { useCart } from "../contexts/CartContext";

// Quick toggleable ingredients: keep it ULTRA simple - 1-2 clicks max
const QUICK_TOGGLES = ["Ντομάτα", "Κρεμμύδι", "Iceberg", "Μουστάρδα", "Ketchup", "Mayo"];

export const ProductModal = ({ item, onClose }) => {
    const { add } = useCart();
    const [qty, setQty] = useState(1);
    const [removed, setRemoved] = useState([]);

    // Find which quick-toggle ingredients exist in this item
    const togglesForItem = useMemo(() => {
        if (!item) return [];
        const desc = (item.description || "").toLowerCase();
        return QUICK_TOGGLES.filter((r) => desc.includes(r.toLowerCase()));
    }, [item]);

    if (!item) return null;

    const toggleRemoved = (ing) =>
        setRemoved((curr) => (curr.includes(ing) ? curr.filter((x) => x !== ing) : [...curr, ing]));

    const totalPrice = item.price * qty;

    const handleAdd = () => {
        const suffix = removed.length ? ` · χωρίς ${removed.join(", ")}` : "";
        const variantId = removed.length
            ? `${item.id}-no-${removed.map((r) => r.toLowerCase()).join("_")}`
            : item.id;
        const displayName = `${item.name}${suffix}`;
        for (let i = 0; i < qty; i++) {
            add({ id: variantId, name: displayName, price: item.price, image_url: item.image_url });
        }
        onClose();
    };

    return (
        <div
            data-testid="product-modal"
            className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-up"
            onClick={onClose}
        >
            <div
                className="relative bg-[#0c0c0e] w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto border-t sm:border border-white/10 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    aria-label="Κλείσιμο"
                    data-testid="modal-close"
                    className="absolute top-3 right-3 z-10 h-10 w-10 grid place-items-center bg-black/70 hover:bg-black/90 rounded-full backdrop-blur-sm"
                >
                    <X size={20} />
                </button>

                <div className="relative aspect-[16/10] overflow-hidden bg-black">
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                        {item.featured && (
                            <span className="inline-flex items-center gap-1.5 bg-brand-yellow text-black text-[10px] font-heading uppercase tracking-widest px-2.5 py-1 font-black mb-2 rounded-full">
                                <Flame size={10} /> Top Pick
                            </span>
                        )}
                        <h2 className="headline text-3xl sm:text-4xl text-white">{item.name}</h2>
                    </div>
                </div>

                {togglesForItem.length > 0 && (
                    <div className="p-5 sm:p-6">
                        <h3 className="font-heading uppercase tracking-wider text-xs text-brand-yellow mb-3">
                            Αφαίρεσε υλικά (προαιρετικό)
                        </h3>
                        <div className="grid grid-cols-2 gap-2.5">
                            {togglesForItem.map((ing) => {
                                const on = removed.includes(ing);
                                return (
                                    <button
                                        key={ing}
                                        data-testid={`remove-${ing}`}
                                        onClick={() => toggleRemoved(ing)}
                                        className={`flex items-center gap-3 px-4 h-14 rounded-xl border-2 text-left transition active:scale-95 ${
                                            on
                                                ? "border-red-500 bg-red-500/15 text-red-300"
                                                : "border-white/15 text-white/80 hover:border-white/30"
                                        }`}
                                    >
                                        <span className={`h-6 w-6 grid place-items-center rounded-md border-2 shrink-0 ${
                                            on ? "border-red-500 bg-red-500/30" : "border-white/30"
                                        }`}>
                                            {on && <X size={14} strokeWidth={3} />}
                                        </span>
                                        <span className={`text-sm font-bold ${on ? "line-through" : ""}`}>{ing}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Sticky footer */}
                <div className="sticky bottom-0 bg-[#0c0c0e] border-t border-white/10 p-4 flex items-center gap-3">
                    <div className="flex items-center gap-1 border-2 border-white/15 rounded-full p-1 shrink-0">
                        <button
                            data-testid="modal-qty-dec"
                            onClick={() => setQty((q) => Math.max(1, q - 1))}
                            className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10 active:scale-90 transition"
                        >
                            <Minus size={18} strokeWidth={3} />
                        </button>
                        <span className="w-8 text-center font-heading font-black text-lg">{qty}</span>
                        <button
                            data-testid="modal-qty-inc"
                            onClick={() => setQty((q) => q + 1)}
                            className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10 active:scale-90 transition"
                        >
                            <Plus size={18} strokeWidth={3} />
                        </button>
                    </div>
                    <button
                        data-testid="modal-add-btn"
                        onClick={handleAdd}
                        className="flex-1 h-14 flex items-center justify-between px-6 bg-gradient-to-r from-brand-red to-red-500 hover:from-brand-redhover hover:to-red-600 text-white font-heading uppercase tracking-wider text-sm font-black rounded-full transition active:scale-95 shadow-lg shadow-brand-red/30"
                    >
                        <span>Στο καλάθι</span>
                        <span className="text-base">€{totalPrice.toFixed(2)}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
